import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Room, RoomEvent } from 'livekit-client';
import api from '../api';
import {
  Phone, PhoneOff, Mic, Headphones, Bot, User,
  Loader2, ChevronDown, MicOff, Minus
} from 'lucide-react';

// ─── Theme helpers ────────────────────────────────────────────────────────────

const resolveTheme = (mode) => {
  if (mode === 'dark') return 'dark';
  if (mode === 'light') return 'light';
  // 'auto' — respect OS preference
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : '99, 102, 241';
};

// ─── Microphone preflight ─────────────────────────────────────────────────────

// A blocked microphone used to look like a working call: the room connected, the
// agent spoke its greeting, and setMicrophoneEnabled() threw into a catch that
// only set an error the connected call screen never showed. The caller talked
// into nothing. So claim the mic BEFORE any session or room exists, and name the
// real cause — on embeds that is almost always a host page that never delegated
// the permission down to our iframe (allow="microphone").
const isFramed = () => {
  try {
    return window.self !== window.top;
  } catch (_) {
    return true; // cross-origin parent — we are framed
  }
};

const micErrorMessage = (err) => {
  switch (err?.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      // A Permissions Policy block surfaces here too, but only inside a frame the
      // host page did not grant the microphone to.
      return isFramed()
        ? 'Microphone blocked by the page hosting this widget. The embedding iframe needs allow="microphone".'
        : 'Microphone access was denied. Allow it in your browser settings, then try again.';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'No microphone was found on this device.';
    case 'NotReadableError':
    case 'AbortError':
      return 'Your microphone is in use by another app. Close it and try again.';
    default:
      return err?.message || 'Could not access your microphone.';
  }
};

// Resolves to null on success, or a caller-facing message explaining the block.
const preflightMicrophone = async () => {
  if (!navigator.mediaDevices?.getUserMedia) {
    // Also what a Permissions Policy block looks like in some browsers: the API
    // is simply absent from the frame.
    return isFramed()
      ? 'Microphone blocked by the page hosting this widget. The embedding iframe needs allow="microphone".'
      : 'Voice calls need a secure (https) connection.';
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // Release it immediately — LiveKit opens its own track on connect.
    stream.getTracks().forEach((track) => track.stop());
    return null;
  } catch (err) {
    return micErrorMessage(err);
  }
};

// ─── Icon selector ────────────────────────────────────────────────────────────

const getButtonIcon = (iconName, className = 'w-6 h-6') => {
  const props = { className };
  switch (iconName) {
    case 'mic': return <Mic {...props} />;
    case 'headphones': return <Headphones {...props} />;
    case 'bot': return <Bot {...props} />;
    case 'user': return <User {...props} />;
    case 'phone':
    default: return <Phone {...props} />;
  }
};

// ─── Position style helper ────────────────────────────────────────────────────

const positionStyle = (position) => {
  switch (position) {
    case 'bottom-left':  return { bottom: 24, left: 24 };
    case 'top-right':    return { top: 24, right: 24 };
    case 'top-left':     return { top: 24, left: 24 };
    case 'bottom-right':
    default:             return { bottom: 24, right: 24 };
  }
};

// ─── Initials helper ──────────────────────────────────────────────────────────

const getInitials = (name = '') => {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

const normalizeColor = (hex, fallback = '#6366f1') => {
  if (!hex || typeof hex !== 'string') return fallback;
  const h = hex.startsWith('#') ? hex : `#${hex}`;
  return /^#[0-9A-Fa-f]{6}$/.test(h) ? h : fallback;
};

const isColorLight = (hex) => {
  const c = normalizeColor(hex);
  const r = parseInt(c.slice(1, 3), 16);
  const g = parseInt(c.slice(3, 5), 16);
  const b = parseInt(c.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.65;
};

/** Pick waveform/spinner color that contrasts with the voice panel background */
const accentColorForPanel = (primary, button, isDarkBg) => {
  const p = normalizeColor(primary);
  const b = normalizeColor(button);
  if (isDarkBg) {
    return isColorLight(p) ? p : (isColorLight(b) ? b : '#ffffff');
  }
  if (!isColorLight(p)) return p;
  if (!isColorLight(b)) return b;
  return '#6366f1';
};

// ─── Live audio waveform (same as standalone voice widget) ─────────────────────

const WAVEFORM_SIZES = {
  default: {
    bars: [4, 13, 5, 9, 15, 4, 11],
    barWidth: 4,
    gap: 3,
    containerHeight: 24,
    heightScale: 1.6,
    idleHeight: 4,
  },
  large: {
    bars: [4, 14, 5, 9, 17, 4, 12, 18, 5, 8, 15, 4, 10, 16, 5, 9],
    barWidth: 5,
    gap: 4,
    containerHeight: 40,
    heightScale: 2.2,
    idleHeight: 4,
  },
};

/** 3 distinct multi-stop keyframe shapes so bars don't all breathe in unison — mimics real mic jitter */
const WAVE_VARIANTS = ['vwWaveA', 'vwWaveB', 'vwWaveC'];

const AudioWaveform = ({ isActive, color, size = 'default' }) => {
  const preset = WAVEFORM_SIZES[size] || WAVEFORM_SIZES.default;
  const barColor = normalizeColor(color);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: preset.gap,
        height: preset.containerHeight,
        width: '100%',
        maxWidth: size === 'large' ? 280 : undefined,
      }}
    >
      {preset.bars.map((h, i) => (
        <div
          key={i}
          style={{
            width: preset.barWidth,
            borderRadius: 9999,
            backgroundColor: barColor,
            height: isActive ? Math.round(h * preset.heightScale) : preset.idleHeight,
            opacity: isActive ? 1 : 0.35,
            transformOrigin: 'center bottom',
            animation: isActive
              ? `${WAVE_VARIANTS[i % WAVE_VARIANTS.length]} ${1.9 + (i % 7) * 0.28}s ease-in-out infinite`
              : 'none',
            animationDelay: `${(i % 9) * 0.1}s`,
          }}
        />
      ))}
    </div>
  );
};

const ConnectingSpinner = ({ color, size = 52 }) => {
  const c = normalizeColor(color);
  const rgb = hexToRgb(c);
  return (
  <div
    style={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    role="status"
    aria-label="Connecting"
  >
    <div
      style={{
        position: 'absolute',
        inset: 0,
        borderRadius: '50%',
        border: `3px solid rgba(${rgb}, 0.2)`,
        borderTopColor: c,
        animation: 'connectSpin 0.75s linear infinite',
      }}
    />
    <div
      style={{
        position: 'absolute',
        inset: 6,
        borderRadius: '50%',
        border: `2px solid rgba(${rgb}, 0.15)`,
        borderBottomColor: c,
        animation: 'connectSpin 1.1s linear infinite reverse',
      }}
    />
    <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: c, opacity: 0.9 }} />
  </div>
  );
};

const CompactVoiceAnimations = () => (
  <style>{`
    @keyframes vwWaveA {
      0% { transform: scaleY(0.3); }
      30% { transform: scaleY(1); }
      55% { transform: scaleY(0.5); }
      80% { transform: scaleY(0.9); }
      100% { transform: scaleY(0.3); }
    }
    @keyframes vwWaveB {
      0% { transform: scaleY(0.9); }
      25% { transform: scaleY(0.4); }
      50% { transform: scaleY(1); }
      75% { transform: scaleY(0.3); }
      100% { transform: scaleY(0.9); }
    }
    @keyframes vwWaveC {
      0% { transform: scaleY(0.4); }
      35% { transform: scaleY(0.8); }
      60% { transform: scaleY(0.3); }
      85% { transform: scaleY(1); }
      100% { transform: scaleY(0.4); }
    }
    @keyframes connectSpin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes vwAvatarPulse {
      0% { transform: scale(.85); opacity: .5; }
      100% { transform: scale(3.6); opacity: 0; }
    }
  `}</style>
);

// ─── Main widget component ────────────────────────────────────────────────────

const VoiceCallWidget = forwardRef(({
  agentId,
  viewMode: initialViewMode,
  hideHeader = false,
  themeOverride = null,
  onCallStateChange = null,
  prefetchedAgent = null,
}, ref) => {
  // ── State ──────────────────────────────────────────────────────────────────
  const [agent, setAgent] = useState(prefetchedAgent);
  const [loading, setLoading] = useState(!prefetchedAgent);
  const [error, setError] = useState(null);

  const [isOpen, setIsOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [callError, setCallError] = useState(null);
  const [captions, setCaptions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [callDuration, setCallDuration] = useState(0);

  const roomRef = useRef(null);
  const audioRef = useRef(null);
  const captionsEndRef = useRef(null);
  const durationRef = useRef(null);

  // ── Parse view mode ────────────────────────────────────────────────────────
  const [viewMode] = useState(() => {
    if (initialViewMode) return initialViewMode;
    if (typeof window === 'undefined') return 'button';
    const params = new URLSearchParams(window.location.search);
    const m = params.get('view');
    if (m === 'panel') return 'panel';
    if (m === 'inline') return 'inline';
    if (m === 'full') return 'full';
    if (m === 'headless') return 'headless';
    return 'button'; // default: floating button
  });

  // ── Embed token ────────────────────────────────────────────────────────────
  const embedToken = useRef(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token') || params.get('embed_token') || params.get('key');
    if (t) embedToken.current = t;
  }, []);

  // ── Load agent ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (prefetchedAgent) {
      setAgent(prefetchedAgent);
      setLoading(false);
      return;
    }
    if (!agentId) { setLoading(false); return; }
    api.get(`/agents/${agentId}`)
      .then(r => setAgent(r.data))
      .catch(() => setError('Agent not found'))
      .finally(() => setLoading(false));
  }, [agentId, prefetchedAgent]);

  // ── Derive theme values (themeOverride from combo embed takes precedence) ───
  const theme = resolveTheme(themeOverride?.mode ?? agent?.voice_widget_theme_mode ?? 'light');
  const isDark = theme === 'dark';
  const primaryColor   = themeOverride?.primaryColor   ?? agent?.voice_widget_primary_color   ?? '#6366f1';
  const buttonColor    = themeOverride?.buttonColor    ?? agent?.voice_widget_button_color    ?? '#6366f1';
  const avatarColor    = themeOverride?.avatarColor    ?? agent?.voice_widget_avatar_color    ?? '#6366f1';
  const logoUrl        = themeOverride?.logoUrl        || null;
  const buttonLabel    = agent?.voice_widget_button_label    || 'Talk to us';
  const showLabel      = agent?.voice_widget_show_label      ?? true;
  const widgetTitle    = themeOverride?.title || agent?.voice_widget_title || agent?.name || 'Voice Assistant';
  const widgetSubtitle = agent?.voice_widget_subtitle        || 'Click to start talking';
  const widgetIcon     = agent?.voice_widget_icon            || 'phone';
  const pulseAnim      = agent?.voice_widget_pulse_animation ?? true;
  const avatarType     = agent?.voice_widget_avatar_type     || 'icon';
  const welcomeMsg     = agent?.voice_widget_welcome_message || null;
  const showTranscript = themeOverride?.showTranscript ?? agent?.voice_widget_show_transcript ?? true;
  const position       = agent?.voice_widget_button_position || 'bottom-right';

  const primaryRgb = hexToRgb(primaryColor);

  // ── Transparent bg for button-mode so it floats over the client site ───────
  useEffect(() => {
    if (viewMode !== 'button') return;
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prev = [html.style.background, body.style.background, root?.style?.background];
    html.style.background = 'transparent';
    body.style.background = 'transparent';
    if (root) root.style.background = 'transparent';
    return () => {
      html.style.background = prev[0];
      body.style.background = prev[1];
      if (root) root.style.background = prev[2] || '';
    };
  }, [viewMode]);

  // ── Auto-scroll captions ───────────────────────────────────────────────────
  useEffect(() => {
    captionsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [captions]);

  // ── Call duration timer ────────────────────────────────────────────────────
  useEffect(() => {
    if (isConnected) {
      setCallDuration(0);
      durationRef.current = setInterval(() => setCallDuration(d => d + 1), 1000);
    } else {
      clearInterval(durationRef.current);
    }
    return () => clearInterval(durationRef.current);
  }, [isConnected]);

  // ── Re-attach any already-published audio tracks whenever the audio element mounts
  // This guards against the race where TrackSubscribed fired before the audio element existed.
  useEffect(() => {
    if (!isConnected || !roomRef.current || !audioRef.current) return;
    roomRef.current.remoteParticipants.forEach((participant) => {
      participant.audioTrackPublications.forEach((pub) => {
        if (pub.track && pub.isSubscribed) {
          pub.track.attach(audioRef.current);
        }
      });
    });
  }, [isConnected]);

  const formatDuration = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // ── Add caption (deduplicated) ─────────────────────────────────────────────
  const addCaption = useCallback((speaker, text) => {
    if (!text?.trim()) return;
    setCaptions(prev => {
      const last = prev[prev.length - 1];
      if (last?.speaker === speaker && last?.text === text) return prev;
      return [...prev, { speaker, text, ts: new Date().toLocaleTimeString() }];
    });
  }, []);

  // ── Start call ─────────────────────────────────────────────────────────────
  const startCall = async () => {
    if (!agent) return;
    setIsConnecting(true);
    setCallError(null);
    setCaptions([]);

    // Fail before a session exists if we cannot have the mic at all.
    const micBlocked = await preflightMicrophone();
    if (micBlocked) {
      setCallError(micBlocked);
      setIsConnecting(false);
      return;
    }

    try {
      const roomName = `voice-widget-${agent.id}-${Date.now()}`;

      const sessionRes = await api.post(
        '/sessions/embed/start',
        { room_name: roomName, agent_id: agent.id, channel: 'test' },
        embedToken.current ? { headers: { 'X-Embed-Token': embedToken.current } } : undefined
      );
      setSessionId(sessionRes.data.session_id);

      const tokenRes = await api.post('/token', { room: roomName, identity: 'widget-user' });
      const { url, token } = tokenRes.data;

      const newRoom = new Room({ adaptiveStream: true, dynacast: true });

      newRoom.on(RoomEvent.Connected, () => {
        setIsConnected(true);
        setIsConnecting(false);
      });

      newRoom.on(RoomEvent.Disconnected, () => {
        setIsConnected(false);
        setIsConnecting(false);
      });

      newRoom.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind === 'audio') {
          // audioRef.current is always mounted at the widget root — safe to attach
          if (audioRef.current) {
            track.attach(audioRef.current);
          }
        }
      });

      newRoom.on(RoomEvent.DataReceived, (payload) => {
        try {
          const data = JSON.parse(new TextDecoder().decode(payload));
          if (data.type === 'transcription' || data.type === 'conversation_item') {
            addCaption(data.speaker || 'agent', data.text);
          }
        } catch (_) {}
      });

      await newRoom.connect(url, token);
      roomRef.current = newRoom;

      try {
        await newRoom.localParticipant.setMicrophoneEnabled(true);
      } catch (micErr) {
        // Preflight passed but publishing still failed. Tear the call down rather
        // than sitting in a room the agent can talk into and never hear back.
        await newRoom.disconnect().catch(() => {});
        roomRef.current = null;
        throw new Error(micErrorMessage(micErr));
      }

    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to start call';
      setCallError(msg);
      setIsConnecting(false);
      // Close the session record we may have already opened, so a failed start
      // does not leave a phantom call in the history.
      setSessionId((openId) => {
        if (openId) api.post(`/sessions/${openId}/end`).catch(() => {});
        return null;
      });
    }
  };

  // ── End call ───────────────────────────────────────────────────────────────
  const endCall = async () => {
    try {
      if (roomRef.current) {
        await roomRef.current.disconnect();
        roomRef.current = null;
      }
      if (sessionId) {
        await api.post(`/sessions/${sessionId}/end`).catch(() => {});
      }
    } finally {
      setIsConnected(false);
      setIsConnecting(false);
      setSessionId(null);
      setIsMuted(false);
      setCallDuration(0);
    }
  };

  useImperativeHandle(ref, () => ({
    startCall,
    endCall,
    isConnected,
    isConnecting,
    isReady: !loading && !!agent,
  }), [isConnected, isConnecting, sessionId, agent, loading, startCall, endCall]);

  useEffect(() => {
    onCallStateChange?.({ isConnected, isConnecting });
  }, [isConnected, isConnecting, onCallStateChange]);

  // ── Toggle mute ────────────────────────────────────────────────────────────
  const toggleMute = async () => {
    if (!roomRef.current) return;
    const newMuted = !isMuted;
    await roomRef.current.localParticipant.setMicrophoneEnabled(!newMuted);
    setIsMuted(newMuted);
  };

  // ── Cleanup on unmount ─────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (roomRef.current) roomRef.current.disconnect();
      clearInterval(durationRef.current);
    };
  }, []);

  // ── Shared CSS vars injected at widget root ────────────────────────────────
  const cssVars = {
    '--vw-primary': primaryColor,
    '--vw-primary-rgb': primaryRgb,
    '--vw-btn': buttonColor,
    '--vw-avatar': avatarColor,
    '--vw-bg': themeOverride?.bgColor || (isDark ? '#1e1e2e' : '#ffffff'),
    '--vw-bg2': themeOverride?.bgColor2 || (isDark ? '#2a2a3e' : '#f8f9fa'),
    '--vw-border': isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
    '--vw-text': isDark ? '#e2e8f0' : '#1a202c',
    '--vw-text-muted': isDark ? '#94a3b8' : '#64748b',
    '--vw-shadow': isDark
      ? '0 24px 80px rgba(0,0,0,0.6)'
      : '0 24px 80px rgba(0,0,0,0.18)',
  };

  // ── Avatar element ─────────────────────────────────────────────────────────
  const AvatarEl = ({ size = 48, square = false }) => (
    <div
      className="flex items-center justify-center flex-shrink-0 font-semibold select-none"
      style={{
        width: size, height: size,
        borderRadius: square ? Math.round(size * 0.28) : 9999,
        backgroundColor: avatarColor,
        color: '#fff',
        fontSize: size * 0.35,
        overflow: 'hidden',
      }}
    >
      {logoUrl
        ? <img src={logoUrl} alt="" style={{ width: '82%', height: '82%', objectFit: 'contain' }} />
        : avatarType === 'initials'
          ? getInitials(agent?.name || 'VA')
          : getButtonIcon(widgetIcon, `w-${Math.round(size * 0.45 / 4)} h-${Math.round(size * 0.45 / 4)}`)}
    </div>
  );

  // ── Inline / full-page rendered card ──────────────────────────────────────
  const CallCard = ({ compact = false, panelHideHeader = false }) => {
    const isEmbeddedPanel = panelHideHeader;
    const compactVoicePanel = isEmbeddedPanel && !showTranscript;
    const hideIdleBody = compactVoicePanel && !isConnected && !isConnecting && !welcomeMsg && !callError;
    const compactActiveCall = compactVoicePanel && isConnected;
    const compactConnecting = compactVoicePanel && isConnecting;
    const hasTranscriptContent = showTranscript && captions.length > 0;
    const showBody = (!hideIdleBody || hasTranscriptContent) && !compactActiveCall && !compactConnecting;
    const accentColor = isEmbeddedPanel
      ? accentColorForPanel(primaryColor, buttonColor, isDark)
      : normalizeColor(primaryColor);

    return (
    <>
    {isEmbeddedPanel && <CompactVoiceAnimations />}
    <div
      style={{
        ...cssVars,
        backgroundColor: 'var(--vw-bg)',
        borderRadius: isEmbeddedPanel ? 0 : (compact ? 16 : 20),
        overflow: 'hidden',
        boxShadow: compact && !panelHideHeader ? 'var(--vw-shadow)' : 'none',
        border: panelHideHeader ? 'none' : '1px solid var(--vw-border)',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: compact && !isEmbeddedPanel ? 360 : '100%',
        height: isEmbeddedPanel ? '100%' : (panelHideHeader ? '100%' : undefined),
        flex: isEmbeddedPanel ? 1 : undefined,
        minHeight: isEmbeddedPanel ? 0 : undefined,
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* Header */}
      {!panelHideHeader && (
      <div
        style={{
          background: `linear-gradient(135deg, ${primaryColor}, ${buttonColor})`,
          padding: compact ? '16px 18px' : '20px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          position: 'relative',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <AvatarEl size={compact ? 38 : 44} />
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: compact ? 14 : 16, lineHeight: 1.3 }}>
              {widgetTitle}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, marginTop: 2 }}>
              {isConnected ? formatDuration(callDuration)
                : isConnecting ? 'Connecting…'
                : widgetSubtitle}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isConnected && (
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              backgroundColor: '#4ade80',
              boxShadow: '0 0 0 3px rgba(74,222,128,0.3)',
              animation: 'pulse 2s cubic-bezier(0.4,0,0.6,1) infinite',
            }} />
          )}
          {compact && (
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'rgba(255,255,255,0.15)', border: 'none',
                borderRadius: 8, padding: '4px 6px', cursor: 'pointer', color: '#fff',
                display: 'flex', alignItems: 'center',
              }}
              aria-label="Minimize"
            >
              <ChevronDown size={16} />
            </button>
          )}
        </div>
      </div>
      )}

      {/* Body — hidden in combo embed when idle (call button only) */}
      {showBody && !compactActiveCall && (
      <div style={{
        flex: isEmbeddedPanel ? '0 0 auto' : 1,
        padding: hideIdleBody ? 0 : (isEmbeddedPanel ? '12px 16px' : (compact ? '16px 18px' : '20px 24px')),
        display: 'flex',
        flexDirection: 'column',
        gap: isEmbeddedPanel ? 12 : 16,
        backgroundColor: 'var(--vw-bg)',
        overflowY: isEmbeddedPanel ? 'visible' : 'auto',
        minHeight: isEmbeddedPanel ? undefined : (compact ? (showTranscript ? 220 : 120) : (showTranscript ? 300 : 160)),
        maxHeight: isEmbeddedPanel ? undefined : (compact ? (showTranscript ? 320 : 160) : (showTranscript ? 480 : 220)),
      }}>

        {/* Welcome message */}
        {!isConnected && !isConnecting && welcomeMsg && (
          <div style={{
            backgroundColor: `rgba(${primaryRgb},0.08)`,
            border: `1px solid rgba(${primaryRgb},0.2)`,
            borderRadius: 12, padding: '10px 14px',
            color: 'var(--vw-text)',
            fontSize: 13, lineHeight: 1.5,
          }}>
            {welcomeMsg}
          </div>
        )}

        {/* Error */}
        {callError && (
          <div style={{
            backgroundColor: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 10, padding: '8px 12px',
            color: '#ef4444', fontSize: 12,
          }}>
            {callError}
          </div>
        )}

        {/* Waveform + status — hidden in combo embed idle state (call button only) */}
        {(isConnected || isConnecting || !isEmbeddedPanel) && (
        <div style={{ textAlign: 'center', padding: isEmbeddedPanel ? '4px 0' : '8px 0' }}>
          {isConnected ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <AudioWaveform isActive={!isMuted} color={primaryColor} />
              <span style={{ fontSize: 12, color: 'var(--vw-text-muted)' }}>
                {isMuted ? 'Microphone muted' : 'Listening…'}
              </span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              {isConnecting ? (
                <ConnectingSpinner color={accentColor} size={52} />
              ) : (
                <>
                  <div style={{
                    width: 52, height: 52, borderRadius: '50%',
                    backgroundColor: `rgba(${primaryRgb},0.12)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: primaryColor,
                  }}>
                    {getButtonIcon(widgetIcon, 'w-6 h-6')}
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--vw-text-muted)' }}>
                    Ready to call
                  </span>
                </>
              )}
            </div>
          )}
        </div>
        )}

        {/* Transcript */}
        {showTranscript && captions.length > 0 && (
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 8,
            overflowY: 'auto', flex: 1,
          }}>
            <div style={{ fontSize: 11, color: 'var(--vw-text-muted)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              Transcript
            </div>
            {captions.map((c, i) => (
              <div
                key={i}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 2,
                  alignItems: c.speaker === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <div style={{
                  maxWidth: '85%',
                  padding: '8px 12px',
                  borderRadius: c.speaker === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  backgroundColor: c.speaker === 'user'
                    ? `rgba(${primaryRgb},0.15)`
                    : 'var(--vw-bg2)',
                  border: `1px solid ${c.speaker === 'user' ? `rgba(${primaryRgb},0.25)` : 'var(--vw-border)'}`,
                  color: 'var(--vw-text)',
                  fontSize: 13, lineHeight: 1.5,
                }}>
                  {c.text}
                </div>
                <span style={{ fontSize: 10, color: 'var(--vw-text-muted)', padding: '0 4px' }}>
                  {c.speaker === 'user' ? 'You' : 'Agent'} · {c.ts}
                </span>
              </div>
            ))}
            <div ref={captionsEndRef} />
          </div>
        )}
      </div>
      )}

      {/* Compact connecting — combo embed, no transcript */}
      {compactConnecting && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            minHeight: 120,
            padding: '20px 16px',
            backgroundColor: 'var(--vw-bg)',
          }}
        >
          <ConnectingSpinner color={accentColor} size={64} />
        </div>
      )}

      {/* Compact active call — combo embed, no transcript: avatar + status + mute/end */}
      {compactActiveCall && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            minHeight: 120,
            padding: '20px 20px 20px',
            gap: 14,
            backgroundColor: 'var(--vw-bg)',
          }}
        >
          {/* Avatar with pulsing glow rings */}
          <div style={{ position: 'relative', width: 124, height: 124, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              background: `radial-gradient(circle, rgba(${primaryRgb},0.35) 0%, rgba(${primaryRgb},0.12) 45%, rgba(${primaryRgb},0) 75%)`,
            }} />
            <div style={{
              position: 'absolute', width: 62, height: 62, borderRadius: '50%',
              background: `rgba(${primaryRgb},0.45)`,
              animation: 'vwAvatarPulse 4s ease-out infinite',
            }} />
            <AvatarEl size={62} />
          </div>

          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
              {widgetTitle}
            </div>
            <div style={{ color: '#4ade80', fontWeight: 600, fontSize: 12, marginTop: 3 }}>
              Connected · {formatDuration(callDuration)}
            </div>
          </div>

          <AudioWaveform isActive={!isMuted} color={primaryColor} size="large" />

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
            <button
              onClick={toggleMute}
              type="button"
              style={{
                border: `1.5px solid ${isMuted ? 'rgba(239,68,68,0.5)' : `rgba(${primaryRgb},0.35)`}`,
                borderRadius: 9999,
                padding: '9px 22px',
                background: isMuted ? 'rgba(239,68,68,0.12)' : 'transparent',
                color: isMuted ? '#ef4444' : 'var(--vw-text)',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {isMuted ? 'Unmute' : 'Mute'}
            </button>
            <button
              onClick={endCall}
              title="End call"
              type="button"
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: accentColor,
                color: isColorLight(normalizeColor(accentColor)) ? '#0f1e2e' : '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Minus size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Controls */}
      {!compactActiveCall && !compactConnecting && (
      <div style={{
        borderTop: hideIdleBody ? 'none' : '1px solid var(--vw-border)',
        backgroundColor: 'var(--vw-bg)',
        padding: hideIdleBody ? '16px' : (isEmbeddedPanel ? '10px 16px' : (compact ? '12px 18px' : '16px 24px')),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        flexShrink: 0,
      }}>
        {isConnected ? (
          <>
            {/* Mute toggle */}
            <button
              onClick={toggleMute}
              title={isMuted ? 'Unmute' : 'Mute'}
              style={{
                width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer',
                backgroundColor: isMuted ? 'rgba(239,68,68,0.15)' : `rgba(${primaryRgb},0.12)`,
                color: isMuted ? '#ef4444' : primaryColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s',
              }}
            >
              {isMuted ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
            {/* End call */}
            <button
              onClick={endCall}
              title="End call"
              style={{
                width: 56, height: 56, borderRadius: '50%', border: 'none', cursor: 'pointer',
                backgroundColor: '#ef4444',
                color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 16px rgba(239,68,68,0.4)',
                transition: 'all 0.2s',
              }}
            >
              <PhoneOff size={22} />
            </button>
          </>
        ) : (
          <button
            onClick={startCall}
            disabled={isConnecting || !agent}
            title="Start call"
            style={{
              width: 60, height: 60, borderRadius: '50%', border: 'none',
              cursor: isConnecting || !agent ? 'not-allowed' : 'pointer',
              backgroundColor: isConnecting || !agent ? '#94a3b8' : buttonColor,
              color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: isConnecting || !agent ? 'none' : `0 4px 20px rgba(${hexToRgb(buttonColor)},0.45)`,
              transition: 'all 0.2s',
              transform: isConnecting ? 'scale(0.95)' : 'scale(1)',
            }}
          >
            {isConnecting
              ? <ConnectingSpinner color={primaryColor} size={28} />
              : <Phone size={24} />}
          </button>
        )}
      </div>
      )}

    </div>
    </>
    );
  };

  // ── Loading / error states ─────────────────────────────────────────────────
  if (loading && viewMode !== 'headless') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40 }}>
        <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
      </div>
    );
  }

  if (error && !agent && viewMode !== 'headless') {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#ef4444', fontSize: 14 }}>
        {error}
      </div>
    );
  }

  // ── Headless mode (call logic only — used by logo voice widget) ────────────
  if (viewMode === 'headless') {
    return (
      <div style={{ display: 'none' }} aria-hidden="true">
        <audio ref={audioRef} autoPlay playsInline style={{ display: 'none' }} />
      </div>
    );
  }

  // ── Full-page mode ─────────────────────────────────────────────────────────
  if (viewMode === 'full') {
    return (
      <div style={{
        minHeight: '100vh',
        background: isDark
          ? 'linear-gradient(135deg, #0f0f1a, #1a1a2e)'
          : 'linear-gradient(135deg, #f0f4ff, #f8f9fa)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
        ...cssVars,
      }}>
        {/* Audio element lives here — never inside CallCard — so it survives all re-renders */}
        <audio ref={audioRef} autoPlay playsInline style={{ display: 'none' }} />
        <div style={{ width: '100%', maxWidth: 440 }}>
          <CallCard compact={false} />
        </div>
      </div>
    );
  }

  // ── Panel mode (embedded inside combo widget) ──────────────────────────────
  if (viewMode === 'panel') {
    return (
      <div style={{ width: '100%', height: '100%', padding: 0, display: 'flex', flexDirection: 'column', ...cssVars }}>
        <style>{`
          @keyframes vwWaveA {
            0% { transform: scaleY(0.3); }
            30% { transform: scaleY(1); }
            55% { transform: scaleY(0.5); }
            80% { transform: scaleY(0.9); }
            100% { transform: scaleY(0.3); }
          }
          @keyframes vwWaveB {
            0% { transform: scaleY(0.9); }
            25% { transform: scaleY(0.4); }
            50% { transform: scaleY(1); }
            75% { transform: scaleY(0.3); }
            100% { transform: scaleY(0.9); }
          }
          @keyframes vwWaveC {
            0% { transform: scaleY(0.4); }
            35% { transform: scaleY(0.8); }
            60% { transform: scaleY(0.3); }
            85% { transform: scaleY(1); }
            100% { transform: scaleY(0.4); }
          }
          @keyframes connectSpin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
        <audio ref={audioRef} autoPlay playsInline style={{ display: 'none' }} />
        <CallCard compact={true} panelHideHeader={hideHeader} />
      </div>
    );
  }

  // ── Inline mode ────────────────────────────────────────────────────────────
  if (viewMode === 'inline') {
    return (
      <div style={{ width: '100%', padding: 0, ...cssVars }}>
        <audio ref={audioRef} autoPlay playsInline style={{ display: 'none' }} />
        <CallCard compact={true} />
      </div>
    );
  }

  // ── Floating button mode (default) ────────────────────────────────────────
  return (
    <>
      {/* Keyframe styles injected once */}
      <style>{`
        @keyframes vwWaveA {
          0% { transform: scaleY(0.3); }
          30% { transform: scaleY(1); }
          55% { transform: scaleY(0.5); }
          80% { transform: scaleY(0.9); }
          100% { transform: scaleY(0.3); }
        }
        @keyframes vwWaveB {
          0% { transform: scaleY(0.9); }
          25% { transform: scaleY(0.4); }
          50% { transform: scaleY(1); }
          75% { transform: scaleY(0.3); }
          100% { transform: scaleY(0.9); }
        }
        @keyframes vwWaveC {
          0% { transform: scaleY(0.4); }
          35% { transform: scaleY(0.8); }
          60% { transform: scaleY(0.3); }
          85% { transform: scaleY(1); }
          100% { transform: scaleY(0.4); }
        }
        @keyframes vwPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(var(--vw-btn-rgb, 99,102,241), 0.4); }
          50% { box-shadow: 0 0 0 12px rgba(var(--vw-btn-rgb, 99,102,241), 0); }
        }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes vwSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

      {/* Audio element at root — always mounted regardless of open/closed state */}
      <audio ref={audioRef} autoPlay playsInline style={{ display: 'none', position: 'absolute' }} />

      {/* Expanded call card */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            width: 360,
            zIndex: 9999998,
            animation: 'vwSlideUp 0.25s cubic-bezier(0.34,1.56,0.64,1)',
            ...positionStyle(position),
            // Offset upward from button
            ...(position.startsWith('bottom')
              ? { bottom: (positionStyle(position).bottom || 24) + 72 }
              : { top: (positionStyle(position).top || 24) + 72 }),
          }}
        >
          <CallCard compact={true} />
        </div>
      )}

      {/* Launcher button */}
      <div
        style={{
          position: 'fixed',
          zIndex: 9999999,
          display: 'flex',
          flexDirection: position.startsWith('bottom') ? 'column' : 'column-reverse',
          alignItems: position.endsWith('right') ? 'flex-end' : 'flex-start',
          gap: 8,
          ...positionStyle(position),
          '--vw-btn-rgb': hexToRgb(buttonColor),
        }}
      >
        {showLabel && !isOpen && !isConnected && (
          <div style={{
            backgroundColor: isDark ? '#2a2a3e' : '#fff',
            border: '1px solid var(--vw-border)',
            borderRadius: 20, padding: '6px 14px',
            fontSize: 13, fontWeight: 600,
            color: 'var(--vw-text)',
            boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
            whiteSpace: 'nowrap',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}>
            {buttonLabel}
          </div>
        )}
        <button
          onClick={() => setIsOpen(o => !o)}
          aria-label={isOpen ? 'Minimize voice widget' : 'Open voice widget'}
          style={{
            width: 60, height: 60, borderRadius: '50%', border: 'none', cursor: 'pointer',
            backgroundColor: buttonColor,
            color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 4px 20px rgba(${hexToRgb(buttonColor)},0.45)`,
            animation: pulseAnim && !isOpen && !isConnected ? 'vwPulse 2s ease-in-out infinite' : 'none',
            transition: 'background-color 0.3s, transform 0.2s',
            transform: isOpen ? 'scale(0.95)' : 'scale(1)',
            fontFamily: 'system-ui',
          }}
        >
          {isOpen
            ? <ChevronDown size={26} />
            : getButtonIcon(widgetIcon, 'w-6 h-6')}
        </button>
      </div>
    </>
  );
});

VoiceCallWidget.displayName = 'VoiceCallWidget';

// ─── Route-param wrapper (used from index.js) ─────────────────────────────────

export const VoiceCallWidgetPage = ({ agentId }) => {
  return <VoiceCallWidget agentId={agentId} />;
};

export default VoiceCallWidget;
